<template>
  <div class="slide-type-list">
    <div
      v-for="typeObj in allTypeSections"
      :key="typeObj.type"
      class="overflow-hidden"
      :class="{
        [`slide-type-list-section-${typeObj.sectionKey}`]: true,
        'hidden': typeObj.sectionKey === 'hackathon' && !canUseHackathon2025Screen,
      }"
    >
      <div
        class="slide-type-section-label"
        :class="{
          'quiz': typeObj.sectionKey === 'quiz',
          'poll': typeObj.sectionKey === 'poll',
          'content': typeObj.sectionKey === 'content',
        }"
      >
        <aha-icon
          :name="typeObj.sectionIcon"
          class="icon mr-2"
          width="21"
          height="21"
          viewBox="0 0 21 21"
        >
        </aha-icon>
        <span class="text-base font-bold">{{ typeObj.sectionLabel }}</span>
      </div>
      <div class="slide-type-section-content" :class="{'quiz': typeObj.sectionKey === 'quiz'}">
        <div
          v-for="(item, index) in typeObj.slideTypes"
          v-show="!item.noType"
          v-tooltip="item.isLegacy ? boxLegacyTooltip : ''"
          :key="item.type + index + item.slideType"
          v-hover-intent="{
            onEnter: () => handleHoverOnSlideType(item, 'over', typeObj.sectionLabel),
            onExit: () => handleHoverOnSlideType(item, 'out', typeObj.sectionLabel)
          }"
          class="box-type"
          v-click-outside="() => clickOutslideUpgrade(item)"
          :class="{
            beta: item.beta,
            new: item.isNew,
            legacy: item.isLegacy,
            updated: item.isUpdated,
            popular: item.isPopular,
            active: isBoxTypeActive(item),
            'is-eu': item.type === 'openEnded' && isEURegion,
            'hidden': item.hidden,
            'smoke-test': item.isSmokeTest,
            'beta-test': item.isBetaTest,
          }"
        >
          <div
            class="absolute top-3 right-3 icon-free"
            @click="handleSelectSlideType({ item, property: wrapperId })"
          >
            <img
              v-if="isForPairPlan(item)"
              v-tooltip="{
                content:htmlTooltip(item),
                trigger: 'click',
                autoHidden: false,
                show: showSlideTypeTooltip === item.name}"
              class="cursor-pointer pointer-events-none"
              src="@/assets/images/pro.svg"
            />
          </div>
          <div
            class="box-content vertical layout center-justified ml-0.25 relative"
            :class="[item.isLegacy ? 'default': 'pointer' , {'opacity-50': isForPairPlan(item)}]"
            @click="handleSelectSlideType({ item, property: wrapperId })"
          >
            <FeatureUpdateTooltipContent
              v-if="isFeatureUpdateTooltipVisible(item)"
              :title="featureTooltipTitle(item.type)"
              :content="featureTooltipContent(item.type)"
            />
            <SlideTypeIcon
              :slide-type="item.type"
              :child-type="item.slideType"
              :left-side="false"
              :active="isBoxTypeActive(item)"
            />
            <div
              v-if="item.type + item.slideType === loadingItem"
              class="loading-show horizontal layout center center-justified"
            >
              <aha-spinner />
            </div>
          </div>
        </div>
      </div>
      <div class="slide-type-section__import" v-if="typeObj.sectionKey === 'quiz'">
        <GenerateFromDoc />
        <ImportSheet />
      </div>
    </div>
    <aha-modal
      @hide="selectedSmokeTestItem = null"
      v-if="selectedSmokeTestItem"
      :title="$i18nPresenter.t('smoke_test_underdevelopment')"
    >
      <p>{{ $i18nPresenter.t('smoke_test_beta_tester') }}</p>
      <template #action>
        <section class="flex gap-4">
          <aha-button @click.stop="signUpSmokeTest()">
            {{ $i18nPresenter.t('Sign me up!') }}
          </aha-button>
          <aha-button variant="white" @click="refuseSignup()">
            {{ $i18nPresenter.t('No thanks') }}
          </aha-button>
        </section>
      </template>
    </aha-modal>
    <aha-modal
      @hide="signedUpSmokeTest = false"
      :title="$i18nPresenter.t('Thank you! Stay tuned for updates from us!')"
      v-if="signedUpSmokeTest"
    >
      <template #action>
        <aha-button @click.stop="signedUpSmokeTest = false">
          {{ $i18nPresenter.t('Close') }}
        </aha-button>
      </template>
    </aha-modal>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { cloneDeep as lodashCloneDeep } from 'lodash'

import * as Sentry from '@sentry/vue'
import shuffleArrayWithSeed from '@/utils/shuffleArrayWithSeed'
import Constant from '@/constant'
import Config from '@/config'
import Util from '@/util'
import eventCategories from '@/constant/eventCategories'
import { SMOKE_TEST_EVENT_ACTIONS } from '@/constant/slideTypes'
import { slideType } from '@/constant/allSlideTypes'
import EventBus from '@/eventBus'

// components
import FeatureUpdateTooltipContent from '@/components-v2/common/tooltip-feature-update/FeatureUpdateTooltipContent.vue'
import PaymentLocalizationMixin from '@/components-v2/my-plan/user-pricing-v2/PaymentLocalizationMixin'
import GenerateFromDoc from '@/components-v2/presentation-editor/new-slide/GenerateFromDoc.vue'
import ImportSheet from '@/components-v2/presentation-editor/import-sheet/ImportSheet.vue'
import SlideTypeIcon from '@/components-v2/common/slide-type-icon/SlideTypeIcon.vue'

// mixins
import SelectSlideTypeMixin from '@/mixins/slide/SelectSlideTypeMixin'
import GoogleSlideMixin from '@/mixins/slide/GoogleSlideMixin'

import UndoRedoMixin from '@/mixins/undo-redo/UndoRedoMixin'

// assets
import imageSubmissionTooltipImage from
  '@/assets/images/feature-update/image-submission-feature-update.svg'
import pollTooltipVideo from '@/assets/images/feature-update/poll.webm'
import imageMergePickImage from '@/assets/images/onboarding/merge-pick-image.gif'

// constants
const keyCountOpenEndedUpdateSlide = 'slide-type-open-ended-feature-update'
const keyCountPollUpdate = 'slide-type-poll-update'
const MAX_TIMES_HOVER_BADGE = 10

const mixins = [UndoRedoMixin, SelectSlideTypeMixin, PaymentLocalizationMixin]

if (Config.integrationPlatform === 'GoogleSlide') {
  mixins.push(GoogleSlideMixin)
}

export const SLIDES_FEATURE_FLAG = {
  CATEGORISE: 'release-categorise',
}

export default {
  components: {
    FeatureUpdateTooltipContent,
    ImportSheet,
    GenerateFromDoc,
    SlideTypeIcon,
  },
  mixins,
  props: {
    wrapperId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      allTypeSections: [],
      imageSubmissionTooltipImage,
      pollTooltipVideo,
      loadingItem: null,
      showSlideTypeTooltip: '',
      count: 0,
      selectedSmokeTestItem: null,
      signedUpSmokeTest: false,
      smokeTestHoverTimeOutId: null,
      imageMergePickImage,
      randomSeed: Math.random(),
    }
  },

  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      currentUser: (state) => state.currentUser,
      presentation: (state) => state.presentation,
      plan: (state) => state.currentUser.plan,
      isOpenModalUpgradePlan: (state) => state.isOpenModalUpgradePlan,
    }),
    ...mapGetters('featureToggles', [
      'isFeatureEnabled',
      'isLoading',
    ]),
    ...mapGetters('embedSlide', [
      'canUseEmbedSlide',
    ]),
    ...mapGetters('hackathong2025', [
      'canUseHackathon2025Screen',
    ]),
    ...mapGetters('slideTypeList', ['getSlideTypeList']),
    isEURegion() {
      return Config.region === Constant.euRegion
    },
    boxLegacyTooltip() {
      return 'This type is no longer available. Try <strong>‘Content’</strong> instead!'
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    '$store.state.currentUser.id': function () {
      this.handleShowUpdateBadge()
    },
    showSlideTypeTooltip(val) {
      const self = this
      if (val.length > 0) {
        const buttons = document.getElementsByClassName('button-upgrade')
        setTimeout(() => {
          Array.from(buttons).forEach((button) => {
            button.addEventListener('click', () => {
              self.openPlanUpgradeModal()
            })
          })
        }, 300)
      }
    },
  },

  created() {
    const allTypeSections = lodashCloneDeep(this.getSlideTypeList)
    const quiz = allTypeSections.find((section) => section.sectionKey === 'quiz')
    const categorise = quiz.slideTypes.find((slide) => slide.slideType === slideType.categorise)
    categorise.isBetaTest = this.isFeatureEnabled(SLIDES_FEATURE_FLAG.CATEGORISE)

    this.shuffleSmokeTestSlide(allTypeSections)
    this.allTypeSections = this.allTypeSections.concat(allTypeSections).map((item) => {
      item.title = this.$i18nPresenter.t(item.title)
      item.desc = this.$i18nPresenter.t(item.desc)

      return item
    })

    if (!this.canUseEmbedSlide) {
      this.allTypeSections = this.allTypeSections.map((section) => {
        if (section.sectionKey === 'content') {
          return {
            ...section,
            slideTypes: section.slideTypes.filter((slide) => slide.type !== 'embed'),
          }
        }
        return section
      })
    }

    this.preloadImageSubmissionFeatureImage()
    this.handleShowUpdateBadge()
  },

  mounted() {
    this.registerHistory('SideLeft')
  },

  methods: {
    ...mapActions([
      'patchCurrentUserAction',
    ]),
    shuffleSmokeTestSlide(allTypeSections) {
      const quizSection = allTypeSections.find((section) => section.sectionKey === 'quiz')
      const slideTypes = quizSection.slideTypes.filter((slide) => !slide.isSmokeTest)
      const smokeTestSlideTypes = quizSection.slideTypes.filter((slide) => slide.isSmokeTest)

      quizSection.slideTypes = [...slideTypes, ...shuffleArrayWithSeed(smokeTestSlideTypes, this.currentUser.id)]
    },
    signUpSmokeTest() {
      const trackData = {
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        eventAction: SMOKE_TEST_EVENT_ACTIONS.SIGNUP,
        presentationId: this.presentation && this.presentation.id,
        slideType: this.selectedSmokeTestItem.type,
      }
      this.selectedSmokeTestItem = null
      this.signedUpSmokeTest = true

      Util.trackGA4AndMixpanel('Click on smoke test slides sign up button', trackData)
    },
    refuseSignup() {
      const trackData = {
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        eventAction: SMOKE_TEST_EVENT_ACTIONS.REFUSE,
        presentationId: this.presentation && this.presentation.id,
        slideType: this.selectedSmokeTestItem.type,
      }

      this.selectedSmokeTestItem = null
      Util.trackGA4AndMixpanel('Click on smoke test slides no thank button', trackData)
    },
    openPlanUpgradeModal() {
      Util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR,
        'Feature upgrade',
        `Limit on the number of slide types > ${this.showSlideTypeTooltip}`)
      return this.$router.push('/apps/user-plan?redirect=/apps/user-plan')
    },
    htmlTooltip(item) {
      return `
      <div class='text-base popover-inner  '>
          <div>
          ${this.contentTooltip(item)}
            </div>
        <button class='aha-button mt-5 aha-button-md aha-button-success button-upgrade' >
          ${this.$i18nPresenter.t('Upgrade')}
        </button>
        </div>
        `
    },
    contentTooltip(item) {
      return item.isForPairPlan
        ? this.$i18nPresenter.t('This feature is not available in your current plan.')
        : this.$i18nPresenter.t("You've reached the maximum number of questions for the Free Plan.")
    },
    clickOutslideUpgrade(item) {
      if (this.showSlideTypeTooltip.length > 0 && item.name === this.showSlideTypeTooltip) {
        this.showSlideTypeTooltip = ''
      }
    },
    isBoxTypeActive(item) {
      const { type, slideType } = this.slideActive

      return (type === item.type && slideType === item.slideType) ||
          (type === 'multipleChoice' && item.type === 'imageChoice')
    },
    featureTooltipTitle(slideType) {
      if (slideType === 'multipleChoice' || slideType === 'imageChoice') {
        return '<div class="font-bold">👉<span class="pl-1.5">Feature update:</span></div>'
      }
      if (slideType === 'openEnded' && !this.isEURegion) {
        return `
          <div class="font-bold">👉 A new way to answer:</div>
        `
      }

      return ''
    },
    featureTooltipContent(slideType) {
      if ((slideType === 'multipleChoice' || slideType === 'imageChoice')) {
        return `
          <div class="w-40">
            <p class="pb-1">The slide types 'Multiple Choice' and 'Image Choice' have been merged into <strong>'Poll'</strong>. Your content is unchanged.</p>
            <video width="174" height="92" id="poll-video" autoplay>
              <source src='${pollTooltipVideo}' type="video/webm">
              Your browser does not support the video tag.
            </video>
          </div>
        `
      }
      if (slideType === 'openEnded' && !this.isEURegion) {
        return `
         <div class="w-40">
            <p class="pb-1">Participants can now respond with photos.</p>
            <img src='${imageSubmissionTooltipImage}' width="174" height="92" />
          </div>
        `
      }

      return ''
    },
    isFeatureUpdateTooltipVisible(slide) {
      if (slide.isUpdated) {
        if (slide.type === 'openEnded' && this.isEURegion) {
          return false
        }

        return true
      }

      return false
    },

    _getKeyUpdateBadge(slideType, isNewKey = false) {
      let key
      const poll = ['multipleChoice', 'imageChoice']

      if ((poll.includes(slideType))) {
        key = keyCountPollUpdate
      }
      if (slideType === 'openEnded' && !this.euRegion) {
        key = keyCountOpenEndedUpdateSlide
      }
      if (isNewKey) {
        key = `${this.currentUser.id}-${key}`
      }

      return key
    },

    _removeAndSetNewKeyUpdateBadge(key) {
      // Old rule: https://ahaslides.atlassian.net/browse/AHA-6520
      // New rule: https://ahaslides.atlassian.net/browse/AHA-7126
      const keyValue = window.localStorage.getItem(key)
      if (keyValue) {
        // Old rule: MAX_TIMES_HOVER_BADGE = 3
        if (keyValue > 2) {
          window.localStorage.setItem(
            `${this.currentUser.id}-${key}`,
            MAX_TIMES_HOVER_BADGE,
          )
        } else {
          window.localStorage.setItem(`${this.currentUser.id}-${key}`, keyValue)
        }
        // If 2 userIds use same browser, 2nd userId won't show badge if (key) => remove old key
        window.localStorage.removeItem(key)
      }
    },

    async handleShowUpdateBadge() {
      try {
        this.allTypeSections.forEach((item) => {
          item.slideTypes.forEach((slide) => {
            if (!slide.isUpdated) return
            const key = this._getKeyUpdateBadge(slide.type)
            this._removeAndSetNewKeyUpdateBadge(key)
            this._setUpdateBadge(
              slide,
              this._getKeyUpdateBadge(slide.type, true),
            )
          })
        })
      } catch (error) {
        Sentry.captureException(error)
      }
    },

    _setUpdateBadge(slide, key) {
      let count
      if (slide.isUpdated) {
        count = window.localStorage.getItem(key) || 0
        if (count == MAX_TIMES_HOVER_BADGE) {
          slide.isUpdated = false

          return
        }
        if (this.currentUser.id) {
          const timeDeployUpdate = 1647837000000 // 21/03/2022 11:30
          const timeUserCreatedAt = new Date(
            this.currentUser.createdAt,
          ).getTime()
          if (timeDeployUpdate < timeUserCreatedAt) {
            slide.isUpdated = false
          }
        }
      }
    },

    preloadImageSubmissionFeatureImage() {
      Util.preloadImage({ url: imageSubmissionTooltipImage })
    },

    handleHoverOnSlideType(item, mode, sectionLabel) {
      if (this.$store.state.isTouchScreen) return

      if (this.loadingItem) return

      if (item.isUpdated && (item.type === 'multipleChoice' || item.type === 'imageChoice')) {
        this._setPlayPollVideo(mode)
      }

      if (item.isUpdated && mode === 'over') {
        const key = this._getKeyUpdateBadge(item.type, true)
        this._setCountHoverUpdateBadgeToLocalStorage(key)
      }

      this._trackingHoverSlideType(item, mode, sectionLabel, this.wrapperId)

      EventBus.$emit('handleHoverOnSlideType', item, mode, this.wrapperId)
    },

    _trackingHoverSlideType(item, mode, sectionLabel, touchPoint) {
      if (mode !== 'over' && item.isSmokeTest) {
        clearTimeout(this.smokeTestHoverTimeOutId)
        this.smokeTestHoverTimeOutId = null
        return
      }

      // As requirement, delay 1 second before tracking hover event to avoid accidental hover
      // More details: https://ahaslides.atlassian.net/wiki/spaces/AT/pages/738623592/New+Slide+Types+Smoke+Test+2024
      if (item.isSmokeTest) {
        this.smokeTestHoverTimeOutId = setTimeout(() => {
          this._sendHoverTracking(item, sectionLabel, touchPoint)
        }, 1000)
        return
      }

      this._sendHoverTracking(item, sectionLabel, touchPoint)
    },

    _sendHoverTracking(item, sectionLabel, touchPoint) {
      const isTheSlideQuantityLimitReached = this.isForPairPlan(item)
      const eventAction = 'Slide type preview >  Hover'
      const customProps = {
        slideType: item.type,
        touchPoint,
        presentationId: this.presentation && this.presentation.id,
        slideId: this.slideActive && this.slideActive.id,
        isTheSlideQuantityLimitReached,
      }

      Util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        eventAction,
        sectionLabel,
        null,
        customProps,
      )
    },

    _setPlayPollVideo(mode) {
      const vid = document.getElementById('poll-video')
      if (!vid) return
      if (mode === 'over') {
        vid.currentTime = 0
        vid.play().catch(() => {
        })
      } else {
        vid.pause()
      }
    },

    _setCountHoverUpdateBadgeToLocalStorage(key) {
      let count = window.localStorage.getItem(key) || 0
      count++
      if (count <= MAX_TIMES_HOVER_BADGE) {
        window.localStorage.setItem(key, count)
      }
    },

    _handleSmokeTestSlideType(item) {
      this.selectedSmokeTestItem = item

      const trackData = {
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        eventAction: SMOKE_TEST_EVENT_ACTIONS.OPEN,
        presentationId: this.presentation && this.presentation.id,
        slideType: item.type,
      }
      Util.trackGA4AndMixpanel('Click on smoke test slides on slide list', trackData)
    },

    async handleSelectSlideType({ item, property }) {
      if (item.isLegacy || item.disabled) {
        return
      }
      if (item.isSmokeTest) {
        this._handleSmokeTestSlideType(item)
        return
      }

      if (this.isForPairPlan(item)) {
        this.showSlideTypeTooltip = item.name
        return
      }
      this.loadingItem = item.type + item.slideType

      try {
        await this.selectSlideType({ item, property })
      } finally {
        this.loadingItem = null
      }

      if (this.wrapperId === 'slide-search-popup') {
        EventBus.$emit('slide-search-popup:close')
      }
      if (!this.slideActive.type) {
        this.$store.state.isOpenEditSlidePowerPoint = false
      }
    },

    restoreSelectSlideType(payload) {
      this.selectSlideType(payload, false)
    },
    getComponentName(item) {
      if (item.hasToggleTip) return 'OneTimeSlideTypeItem'
      return 'SlideTypeItem'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variable.scss';
@import '@/assets/scss-v2/variables.scss';

.fas,
.far,
.fab {
  color: $dark-icon-color;
}

.title-table-presentation {
  @apply text-sm font-bold;

  .title-table-presentation-tooltip {
    font-size: 14px;
    color: #b7b7b7;
  }
}

.box-content {
  @apply ring-1 ring-inset ring-secondary-40;
  display: flex;
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  padding: 2px 0 0;
  min-height: 72px;
  transition: 0.5s;
  transition-timing-function: cubic-bezier(0.33, 1, 0.68, 1);

  &:hover {
    @apply shadow-lg;
  }
}

.slide-type-section-label {
  padding: 8px 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  background-color: theme('colors.primary.20');

  .icon {
    color: #6A1EBB;
  }
}

.slide-type-section-content {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
  padding-top: 12px;
  padding-bottom: 24px;
  &.quiz {
    padding-bottom: 16px;
  }
}

.slide-type-section__import {
  display: flex;
  padding-bottom: 1.5rem;
}
</style>
